// Question counts for the host's quiz list: downloads each quiz CSV once and counts the
// playable questions (not join / intro / round / break / display / score slides).
import Papa from "papaparse";
import { getDownloadURL } from "firebase/storage";
import { FREE_TEST_EXCLUDED_CATS, questionCat } from "./entitlements";

const NON_QUESTION_CATS = new Set([
  ...FREE_TEST_EXCLUDED_CATS,
  "DISPLAY",
  "SLIDE",
  "DEFAULT",
  "DEFAULT CATEGORY",
  "BREAK TIME",
]);

const CACHE_PREFIX = "quizQCount:";

export function countQuizQuestions(rows) {
  let n = 0;
  for (const row of rows) {
    const cat = questionCat(row);
    if (cat === "END QUIZ") break;
    if (!cat && !String(row?.Q || "").trim()) continue; // blank row
    if (!NON_QUESTION_CATS.has(cat)) n += 1;
  }
  return n;
}

function cacheKey(fileObj) {
  return `${CACHE_PREFIX}${fileObj.name}|${fileObj.updated || ""}`;
}

/** Cached count for this exact version of the file, or null. */
export function getCachedQuestionCount(fileObj) {
  try {
    const v = localStorage.getItem(cacheKey(fileObj));
    return v === null ? null : Number(v);
  } catch {
    return null;
  }
}

export async function fetchQuestionCount(fileObj) {
  const url = await getDownloadURL(fileObj.ref);
  const csv = await (await fetch(url)).text();
  const { data } = Papa.parse(csv, { header: true, skipEmptyLines: true });
  const count = countQuizQuestions(data);
  try {
    localStorage.setItem(cacheKey(fileObj), String(count));
  } catch {}
  return count;
}

/** Fills counts for every quiz, a few downloads at a time, calling onCount(name, count) as each arrives. */
export async function loadQuestionCounts(quizFiles, onCount, isCancelled, concurrency = 4) {
  const queue = [];
  for (const f of quizFiles) {
    const cached = getCachedQuestionCount(f);
    if (cached !== null) onCount(f.name, cached);
    else if (f.ref) queue.push(f);
  }
  const worker = async () => {
    while (queue.length && !isCancelled()) {
      const f = queue.shift();
      try {
        const count = await fetchQuestionCount(f);
        if (!isCancelled()) onCount(f.name, count);
      } catch (err) {
        console.warn("Question count failed for", f.name, err?.message || err);
      }
    }
  };
  await Promise.all(Array.from({ length: concurrency }, worker));
}
